import React, { useEffect, useState } from 'react';
import { Container, Card, Spinner, Table, Button, Alert, Badge } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/axiosConfig';
import { OrganizationDto } from '../types';
import { isAdmin, isClinicHead } from '../auth';
import ClinicModal from '../components/ClinicModal';

const Clinics: React.FC = () => {
    const [orgs, setOrgs] = useState<OrganizationDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const [modalOpen, setModalOpen] = useState(false);
    const [selected, setSelected] = useState<OrganizationDto | null>(null);

    const load = React.useCallback(async () => {
            try {
                // Admin sees every clinic; clinic head sees only orgs they belong to.
                const url = isAdmin() ? '/organizations' : '/organizations/me';
                const res = await api.get<OrganizationDto[]>(url);
                setOrgs(res.data);
                // Clinic head with exactly one org → drop them straight into it.
                if (!isAdmin() && isClinicHead() && res.data.length === 1) {
                    navigate(`/clinics/${res.data[0].id}`, { replace: true });
                }
            } catch (e: any) {
                setError(e.response?.data?.message || 'Не удалось загрузить клиники');
            } finally {
                setLoading(false);
            }
    }, [navigate]);

    useEffect(() => {
        load();
    }, [load]);

    const remove = async (clinic: OrganizationDto) => {
        if (!window.confirm(`Удалить клинику «${clinic.name}»? Это действие нельзя отменить.`)) return;
        try { await api.delete(`/organizations/${clinic.id}`); await load(); }
        catch (e: any) { setError(e.response?.data?.message || 'Не удалось удалить клинику'); }
    };

    if (loading) {
        return (
            <Container className="d-flex justify-content-center mt-5">
                <Spinner animation="border" />
            </Container>
        );
    }

    return (
        <Container className="mt-4">
            <div className="page-heading">
                <div>
                    <h2><i className="bi bi-hospital text-primary me-2"></i>Клиники</h2>
                    <div className="muted-small">Список организаций, использующих TeriAI</div>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Badge bg="light" text="dark" pill className="px-3 py-2 fw-normal">Всего: {orgs.length}</Badge>
                    {isAdmin() && <Button onClick={() => { setSelected(null); setModalOpen(true); }}><i className="bi bi-plus-lg me-1" />Добавить клинику</Button>}
                </div>
            </div>
            {error && <Alert variant="danger">{error}</Alert>}
            <Card className="shadow-sm">
                <Card.Body className="p-0">
                    <Table hover responsive className="mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Название</th>
                                <th>Короткое имя</th>
                                <th>Адрес</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {orgs.map((o) => (
                                <tr key={o.id}>
                                    <td className="fw-medium">{o.name}</td>
                                    <td><code>{o.shortName ?? '—'}</code></td>
                                    <td className="text-muted">{o.address ?? '—'}</td>
                                    <td className="text-end">
                                        {isAdmin() && <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => { setSelected(o); setModalOpen(true); }}><i className="bi bi-pencil" /></Button>}
                                        <Button as={Link as any} to={`/clinics/${o.id}`} size="sm" variant="outline-primary">
                                            Открыть <i className="bi bi-arrow-right ms-1"></i>
                                        </Button>
                                        {isAdmin() && <Button size="sm" variant="outline-danger" className="ms-2" onClick={() => remove(o)}><i className="bi bi-trash" /></Button>}
                                    </td>
                                </tr>
                            ))}
                            {orgs.length === 0 && (
                                <tr><td colSpan={4} className="text-center text-muted py-4">Клиники не найдены</td></tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
            {isAdmin() && <ClinicModal show={modalOpen} clinic={selected} onClose={() => setModalOpen(false)} onSaved={async () => { setModalOpen(false); await load(); }} />}
        </Container>
    );
};

export default Clinics;
